/* eslint-disable react-hooks/exhaustive-deps */
import { EditOutlined, ExclamationCircleFilled, LoadingOutlined } from '@ant-design/icons';
import ConnectFacebookLocation from '@components/ConnectFacebookLocation';
import LoadableContainer from '@components/LoadableContainer';
import SplashScreen from '@components/SplashScreen';
import { useAppDispatch, useAuthState, useReviewState } from '@core/configureStore';
import { activateAutopilot } from '@core/features/business/thunks/activateAutopilot.thunk';
import { deactivateAutopilot } from '@core/features/business/thunks/deactivateAutopilot.thunk';
import { Button, Card, Col, Divider, Radio, Rate, Row, Space, Switch, Typography } from 'antd';
import confirm from 'antd/es/modal/confirm';
import React, { useEffect, useState } from 'react';
import TitleWithText from '@components/TitleWithText';
import OwnerSettingRow from '@components/OwnerSettingRow';
import { AccountContact, DefendResponseType } from '@core/models';
import { updateOwner } from '@core/features/business/thunks/updateOwner';
import { ChatBotLottieFile } from '@components/animations';
import Lottie from 'lottie-react';
import { updateDefendResponse } from '@core/features/business/thunks/updateDefendResponse.thunk';

type Props = {};

const SettingsScreen = (props: Props) => {
  const dispatch = useAppDispatch();
  const { user, isLoading } = useAuthState();
  const { reviewResponse, isLoading: isLoadingReview } = useReviewState();

  const [enablePositivePilot, setEnablePositivePilot] = useState(false);
  const [enableNegativePilot, setEnableNegativePilot] = useState(false);
  const [negativePilotOption, setNegativePilotOption] = useState(DefendResponseType.AI_RESPONSE);
  const [editOwner, setEditOwner] = useState(false);
  const [owner, setOwner] = useState<AccountContact>({
    email: '',
    given_name: '',
    family_name: '',
  } as AccountContact);

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setOwner({
      ...owner,
      [name]: value,
    });
  };

  const handleUpdateOwner = () => {
    dispatch(
      updateOwner({
        ownerEmail: owner.email,
        ownerGivenName: owner.given_name,
        ownerFamilyName: owner.family_name,
      })
    );
  };

  const handleActivateAutopilot = async () => {
    confirm({
      title: 'Activate auto engage mode for positive reviews',
      icon: <ExclamationCircleFilled />,
      content: (
        <div style={{ paddingRight: 20, margin: '0px 0px 20px 0px' }}>
          <Lottie
            style={{
              height: 200,
            }}
            animationData={ChatBotLottieFile}
          />
          Activate auto pilot mode for your positive reviews? This feature enables AI automated
          replies without manual intervention. Confirm to proceed.
        </div>
      ),
      cancelText: 'Cancel',
      okCancel: true,
      onCancel: () => {},
      okText: 'Activate',
      onOk: () => {
        dispatch(activateAutopilot());
        setEnablePositivePilot(true);
      },
    });
  };

  const handleDeactivateAutopilot = async () => {
    await dispatch(deactivateAutopilot());
    setEnablePositivePilot(false);
  };

  const handleUpdateDefendResponse = async (type: DefendResponseType) => {
    switch (type) {
      case DefendResponseType.NONE:
        setEnableNegativePilot(false);
        break;
      case DefendResponseType.AI_RESPONSE:
        setEnableNegativePilot(true);
        setNegativePilotOption(type);
        break;
      case DefendResponseType.RECTIFICATION:
        setEnableNegativePilot(true);
        setNegativePilotOption(type);
        break;
      default:
        break;
    }
    dispatch(updateDefendResponse(type));
  };

  const handleToggleDefend = async (isChecked: boolean) => {
    if (isChecked) {
      confirm({
        title: 'Activate auto engage mode for negative reviews',
        icon: <ExclamationCircleFilled />,
        content: (
          <div style={{ paddingRight: 20, margin: '0px 0px 20px 0px' }}>
            <Lottie
              style={{
                height: 200,
              }}
              animationData={ChatBotLottieFile}
            />
            Activate auto pilot mode for your negative reviews? This feature enables AI automated
            replies, or an alternative rectification process without manual intervention. Confirm to
            proceed.
          </div>
        ),
        cancelText: 'Cancel',
        okCancel: true,
        onCancel: () => {},
        okText: 'Activate',
        onOk: () => {
          handleUpdateDefendResponse(DefendResponseType.AI_RESPONSE);
        },
      });
    } else {
      handleUpdateDefendResponse(DefendResponseType.NONE);
    }
  };

  const getOwnerDetail = () => {
    const ownerContact = user?.selected_location?.owner_contact;
    if (ownerContact) {
      return `${ownerContact.given_name} ${ownerContact.family_name} (${ownerContact.email})`;
    }
    return '';
  };

  useEffect(() => {
    const ownerContact = user?.selected_location?.owner_contact;
    const positiveAutoPilot = user?.selected_location?.auto_pilot_activated;
    const currentDefendType = user?.selected_location.defend_response_type;

    setEnablePositivePilot(positiveAutoPilot || false);

    if (ownerContact) {
      setOwner(ownerContact);
    }

    if (currentDefendType === DefendResponseType.NONE) {
      setEnableNegativePilot(false);
    } else {
      setEnableNegativePilot(true);
      setNegativePilotOption(Number(currentDefendType));
    }
  }, []);

  return (
    <div className="screen-card">
      <h3>Settings</h3>
      <Divider />
      <LoadableContainer isLoading={isLoadingReview || isLoading} loadComponent={<SplashScreen />}>
        <React.Fragment>
          <Row gutter={20} style={{ marginBottom: 20 }}>
            <Col span={12}>
              <Card
                style={{ height: '100%' }}
                className="tablespace"
                title={user?.selected_location?.title}
                extra={
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Rate value={reviewResponse.averageRating} disabled />
                    <Typography style={{ fontSize: '10px' }}>
                      {`Average Rating: ${reviewResponse.averageRating?.toFixed(2) || 'Invalid'}`}
                    </Typography>
                  </div>
                }>
                <Divider style={{ margin: 0, marginBottom: 20 }} />
                <div style={{ padding: '20px 0px' }}>
                  <TitleWithText title="Address">{`${user?.selected_location?.full_address}`}</TitleWithText>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                    }}>
                    <TitleWithText title="Owner">{`${getOwnerDetail()}`}</TitleWithText>
                    <Button
                      type="link"
                      icon={editOwner ? <></> : <EditOutlined />}
                      onClick={() => setEditOwner(!editOwner)}>
                      {editOwner ? 'Cancel' : 'Edit'}
                    </Button>
                  </div>
                  {editOwner && (
                    <OwnerSettingRow
                      owner={owner}
                      handleInputChange={handleInputChange}
                      handleUpdateOwner={handleUpdateOwner}
                    />
                  )}
                </div>
              </Card>
            </Col>
            <Col span={12}>
              <Card className="tablespace" title={'Social Integration'} style={{ width: 'full' }}>
                <Divider style={{ margin: 0, marginBottom: 20 }} />
                <div style={{ padding: '20px 0px' }}>
                  <div>
                    Automatically generate and stress-free post new content on your business social
                    accounts
                  </div>
                  <Space style={{ display: 'flex', marginTop: 20 }}>
                    <ConnectFacebookLocation />
                    {/* <ConnectInstagramLocation /> */}
                  </Space>
                </div>
              </Card>
            </Col>
          </Row>
          <Card
            className="tablespace mb-24"
            title={'Auto Engage'}
            style={{ width: 'full', marginBottom: '10px' }}>
            <Divider style={{ margin: 0, marginBottom: 20 }} />
            <div style={{ padding: '20px 0px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>Enable automatic response for positive reviews (3-5 stars)</div>

                {user?.selected_location && !isLoading ? (
                  <Space>
                    <Switch
                      defaultChecked={user?.selected_location.auto_pilot_activated}
                      checked={enablePositivePilot}
                      onChange={isChecked => {
                        !isChecked ? handleDeactivateAutopilot() : handleActivateAutopilot();
                      }}
                    />
                  </Space>
                ) : (
                  <LoadingOutlined />
                )}
              </div>

              <br />

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>Enable automatic response for negative reviews (1-2 stars)</div>

                <Space>
                  <Switch
                    defaultChecked={
                      user?.selected_location.defend_response_type !== DefendResponseType.NONE
                    }
                    checked={enableNegativePilot}
                    onChange={isChecked => handleToggleDefend(isChecked)}
                  />
                </Space>
              </div>

              <div>
                <Radio.Group
                  onChange={e => handleUpdateDefendResponse(e.target.value)}
                  value={negativePilotOption}>
                  <Radio value={DefendResponseType.AI_RESPONSE} disabled={!enableNegativePilot}>
                    AI Generated Response
                  </Radio>
                  <Radio value={DefendResponseType.RECTIFICATION} disabled={!enableNegativePilot}>
                    Rectification Process
                  </Radio>
                </Radio.Group>
              </div>
            </div>
          </Card>
        </React.Fragment>
      </LoadableContainer>
    </div>
  );
};

export default SettingsScreen;
